import type { ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { isAdminOnlyRouteAllowed } from './lib/adminAccess.ts';
import { useAdminAuth } from './lib/auth/admin.tsx';
import { ConfirmProvider } from './lib/confirm.tsx';
import { ToastProvider } from './lib/toast.tsx';
import { AdminAcceptInvite } from './auth/AdminAcceptInvite.tsx';
import { AdminLogin } from './auth/AdminLogin.tsx';
import { SsoCallback } from './auth/SsoCallback.tsx';
import { AdminNoAccess } from './auth/AdminNoAccess.tsx';
import { ForgotPassword } from './auth/ForgotPassword.tsx';
import { ResetPassword } from './auth/ResetPassword.tsx';
import { NotFound } from '@l2u/ui/auth/NotFound.tsx';
import { LegacyPrefixRedirect } from '@l2u/ui/auth/LegacyPrefixRedirect.tsx';
import { AdminShell } from './admin/AdminShell.tsx';
import { AdminDashboard } from './admin/Dashboard.tsx';
import { AdminCompanies } from './admin/Companies.tsx';
import { AdminCompanyDetail } from './admin/CompanyDetail.tsx';
import { AdminQuestionBuilder } from './admin/QuestionBuilder.tsx';
import { AdminQuestionnaires } from './admin/Questionnaires.tsx';
import { AdminDocuments } from './admin/Documents.tsx';
import { AdminExpiringDocuments } from './admin/ExpiringDocuments.tsx';
import { AdminSettings } from './admin/Settings.tsx';

// Reviewers are redirected to the Dashboard instead of a 403 dead end. Hiding
// the sidebar rows is not access control — this catches typed URLs and stale
// bookmarks. AdminShell renders its Outlet only after auth resolves, so `user`
// is set whenever a child route mounts; the API's requireSuperAdminRole gates
// the data server-side regardless.
function AdminOnly({ children }: { children: ReactNode }) {
  const { user } = useAdminAuth();
  if (!isAdminOnlyRouteAllowed(user?.role)) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export function App() {
  return (
    <ToastProvider>
      <ConfirmProvider>
        <Routes>
          {/* Auth */}
          <Route path="/login" element={<AdminLogin />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/accept-invite" element={<AdminAcceptInvite />} />
          <Route path="/sso/callback" element={<SsoCallback />} />
          {/* Must stay OUTSIDE the AdminShell route: the shell's !user guard
              redirects to /login, so a refused user rendered inside the shell
              would be bounced away from the very screen explaining why. */}
          <Route path="/no-access" element={<AdminNoAccess />} />

          {/* Admin */}
          <Route path="/" element={<AdminShell />}>
            <Route index element={<AdminDashboard />} />
            <Route path="vendors" element={<AdminCompanies />} />
            <Route path="vendors/:id" element={<AdminCompanyDetail />} />
            <Route
              path="questionnaires"
              element={
                <AdminOnly>
                  <AdminQuestionnaires />
                </AdminOnly>
              }
            />
            <Route
              path="questions"
              element={
                <AdminOnly>
                  <AdminQuestionBuilder />
                </AdminOnly>
              }
            />
            <Route
              path="documents"
              element={
                <AdminOnly>
                  <AdminDocuments />
                </AdminOnly>
              }
            />
            <Route path="expiring-documents" element={<AdminExpiringDocuments />} />
            <Route path="users" element={<Navigate to="/settings?tab=users" replace />} />
            <Route
              path="settings"
              element={
                <AdminOnly>
                  <AdminSettings />
                </AdminOnly>
              }
            />
            <Route path="activity" element={<Navigate to="/settings?tab=activity" replace />} />
          </Route>

          {/* Legacy /admin/* — Supabase invite/recovery emails and old bookmarks
              still land on /admin/accept-invite, /admin/reset-password, etc.
              LegacyPrefixRedirect strips the prefix and preserves search + hash so
              the tokens those emails carry survive the redirect. */}
          <Route path="/admin/*" element={<LegacyPrefixRedirect prefix="/admin" />} />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </ConfirmProvider>
    </ToastProvider>
  );
}
